import { Button, Modal, Spacer } from '@loomhq/lens';
import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import uaParser from 'ua-parser-js';

import { setShowBlockedNotificationsModal } from 'actions/notification-settings';
import {
  BLOCKED_NOTIFICATIONS_PROMPT_DISMISSED,
  BLOCKED_NOTIFICATIONS_PROMPT_SHOWN,
} from 'constants/events';
import { PERMISSION_GRANTED } from 'constants/notifications';
import { reenableWebNotifications } from 'creators/notification-settings';
import * as analytics from 'utilities/analytics';
import { registerNewPushSubscription } from 'utilities/notifications';

import chromeBlockedNotificationsImgSrc from 'img/notifications/chrome-notifications-blocked.png';
import edgeBlockedNotificationsImgSrc from 'img/notifications/edge-notifications-blocked.png';
import firefoxBlockedNotificationsImgSrc from 'img/notifications/firefox-notifications-blocked.png';
import safariBlockedNotificationsImgSrc from 'img/notifications/safari-notifications-blocked.png';

const BLOCKED_MODAL_ID = 'block-noties-modal';

const copy = {
  Chrome: {
    content: `Click “Allow” in the notifications dropdown in the chrome menu.`,
    imgSrc: chromeBlockedNotificationsImgSrc,
  },
  Firefox: {
    content: `Click the lock in the top left of the browser to open the drop down then remove the "Blocked" status by clicking the X in the bottom right corner of the drop down.`,
    imgSrc: firefoxBlockedNotificationsImgSrc,
  },
  Edge: {
    content: `Click the padlock in the top right of the browswer then click “Allow” in the notifications dropdown in the edge menu.`,
    imgSrc: edgeBlockedNotificationsImgSrc,
  },
  Safari: {
    content: `Open the safari browser perferences and navigate to websites in the top bar menu then chooose the “Allow” option for "www.loom.com" in the dropdown.`,
    imgSrc: safariBlockedNotificationsImgSrc,
  },
  default: {
    title:
      'You need to adjust the permissions in your browser for push notifications to allow them for the domain "www.loom.com"',
  },
};

const createRootElement = id => {
  const rootContainer = document.createElement('div');

  rootContainer.setAttribute('id', id);

  return rootContainer;
};

const addElmToBody = appendElm => {
  const containerElm = document.getElementById('container');

  if (containerElm) {
    containerElm.appendChild(appendElm);
  }
};

const BlockedNotificationsModal = ({ location = '' }) => {
  const dispatch = useDispatch();
  const portalElmRef = useRef(null);

  const showBlockedBanner = useSelector(
    state => state.notification_settings?.showBlockedBanner
  );

  const setOpen = useCallback(
    newOpenValue => {
      dispatch(setShowBlockedNotificationsModal(newOpenValue));
    },
    [dispatch]
  );

  useEffect(() => {
    const existingRootElm = document.querySelector(`#${BLOCKED_MODAL_ID}`);
    const portalElm = existingRootElm || createRootElement(BLOCKED_MODAL_ID);

    if (!existingRootElm) {
      addElmToBody(portalElm);
    }

    portalElmRef.current = portalElm;

    return function removePortalElm() {
      portalElmRef?.current?.remove();
    };
  }, []);

  useEffect(() => {
    if (!showBlockedBanner) {
      return;
    }

    const checkPermissionInterval = setInterval(async () => {
      if (Notification.permission === PERMISSION_GRANTED) {
        clearInterval(checkPermissionInterval);

        await registerNewPushSubscription('blockedModal');

        dispatch(reenableWebNotifications(true));

        setOpen(false);
      }
    }, 1000);

    return () => {
      clearInterval(checkPermissionInterval);
    };
  }, [dispatch, setOpen, showBlockedBanner]);

  useEffect(() => {
    if (showBlockedBanner) {
      analytics.track(BLOCKED_NOTIFICATIONS_PROMPT_SHOWN, {
        location,
      });
    }
  }, [location, showBlockedBanner]);

  const dismissModal = () => {
    analytics.track(BLOCKED_NOTIFICATIONS_PROMPT_DISMISSED, {
      location,
    });

    setOpen(false);
  };

  const parser = new uaParser();
  const { browser } = parser.getResult();
  const { content, imgSrc } = copy[browser.name] || copy.default;

  if (!portalElmRef.current || !showBlockedBanner) {
    return null;
  }

  return createPortal(
    <Modal
      mainButton={
        <Button onClick={dismissModal} variant="primary">
          Okay cool
        </Button>
      }
      title="You need to adjust the permissions in your browser"
      isOpen={showBlockedBanner}
      onCloseClick={() => setOpen(!showBlockedBanner)}
      onBackgroundClick={() => setOpen(!showBlockedBanner)}
    >
      {content}
      <Spacer bottom="large" />
      {imgSrc && <img src={imgSrc} />}
    </Modal>,
    portalElmRef.current
  );
};

export default BlockedNotificationsModal;
